import React, {useEffect, useMemo, useState} from 'react';
import {
    Alert,
    AppHeader, AppHeaderOption,
    DataList,
    ListItemElement,
    RoundButton,
    Searchbox,
    Spinner
} from "@tag/tag-components-react-v4";
import {
    ArrowRightFilled, CheckboxEmptyFilled, CheckboxEmptyLined, CheckboxTickedFilled, CircleInfoLined,
    Database2Filled,
    FilingCabinetFilled,
    IDLined,
    MenuFilled,
    SearchFilled,
    WeatherCloudLined
} from "@tag/tag-icons";
import CustomerService from "../services/CustomerService";
import {CorpSiteAndCustomers} from "../types/CorpSiteAndCustomers";
import styles from './CustomerList.module.scss';
import {CustomerDTO} from "../types/CustomerDTO";
import HelperMethods from "../../General/services/HelperMethods";
import CustomerMenuModal from "./CustomerMenuModal";
import CustomerInfoModal from "./CustomerInfoModal/CustomerInfoModal";
import { ProjectAccents } from "../../General/config/projectAccents";

function CustomerList() {

    const [searchTerm, setSearchTerm] = useState<string>('');
    const [allCorpSiteAndCustomers, setAllCorpSiteAndCustomers] = useState<CorpSiteAndCustomers[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [alert, setAlert] = useState<{type: any, message: string}>();
    const [selectedCustomerForMenu, setSelectedCustomerForMenu] = useState<CustomerDTO|undefined>();
    const [selectedCustomerForInfo, setSelectedCustomerForInfo] = useState<CustomerDTO|undefined>();

    useEffect(() => {
        loadCustomerData();
    }, []);

    const loadCustomerData = async () => {
        setLoading(true);
        
        const corpSiteAndCustomers = await CustomerService.getAllCustomers();
        setAllCorpSiteAndCustomers(corpSiteAndCustomers);
        
        setLoading(false);
    }
    
    const getCustomerData = (): CustomerDTO[] => {
        if (!searchTerm) {
            return [];
        }
        
        let allCustomers: CustomerDTO[] = allCorpSiteAndCustomers.flatMap(csc =>
            csc.customers.map(customer => ({
                ...customer, // Spread the properties of the Customer object
                corpSite: csc.corpSiteName,
                image: undefined,
                initials: HelperMethods.getInitials(customer.name)
            }))
        );

        return HelperMethods.advancedFuseSearch(searchTerm, allCustomers, ['name', 'clientId', 'databaseName']);
    }

    const customerData = useMemo(
        () => getCustomerData(), 
        [searchTerm, allCorpSiteAndCustomers]
    );
    
    const goToCareComplianceSite = (dataItem: CustomerDTO) => {
        if (process.env.REACT_APP_DOMAIN === 'Cloud' || process.env.REACT_APP_DOMAIN === 'Local')
            window.open(`https://${dataItem.urlName}.${dataItem.urlAccGroup}.accesscarecompliance.com/#/login?identity=false`, '_blank');
        else
            window.open(`https://${dataItem.urlName}.accesscarecompliance.com/#/login?identity=false`, '_blank');
    }

    let content;
    if (loading) {
        content = <Spinner/>
    } else {
        content = <>
            <AppHeader
                heading="Customer List"
                icon={<SearchFilled/>}
                accent={ProjectAccents.CareCompliance}
            >
                <div
                    className={'customer-list-search-container'}
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <Searchbox
                        onSearch={(e) => setSearchTerm(e)}
                        clearButton
                        style={{width: '100%'}}
                    />
                </div>
            </AppHeader>
            <DataList
                avatarProps={{srcField: 'image', placeholderField: 'initials', accent: ProjectAccents.CareCompliance}}
                primaryField="name"
                renderRightElements={(dataItem) => (
                    <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                        <RoundButton
                            icon={<ArrowRightFilled />}
                            accent={ProjectAccents.CareCompliance}
                            onClick={() => goToCareComplianceSite(dataItem)}
                        />
                        <RoundButton
                            icon={<CircleInfoLined />}
                            accent={ProjectAccents.CareCompliance}
                            onClick={() => setSelectedCustomerForInfo(dataItem)}
                        />
                        <RoundButton
                            icon={<MenuFilled />}
                            accent={ProjectAccents.CareCompliance}
                            onClick={() => setSelectedCustomerForMenu(dataItem)}
                        />
                    </div>
                )}
                renderSubItemElements={(dataItem) => (
                    <>
                        <ListItemElement
                            icon={<FilingCabinetFilled accent={ProjectAccents.CareCompliance}/>}
                        >
                            {dataItem.corpSite}
                        </ListItemElement>
                        <ListItemElement
                            icon={<IDLined accent={ProjectAccents.CareCompliance}/>}
                        >
                            {dataItem.clientId}
                        </ListItemElement>
                        <ListItemElement
                            icon={<WeatherCloudLined accent={ProjectAccents.CareCompliance}/>}
                            style={{marginLeft: '2em'}}
                        >
                            {dataItem.accGroup}
                        </ListItemElement>
                        <ListItemElement
                            icon={<Database2Filled accent={ProjectAccents.CareCompliance}/>}
                            style={{marginLeft: '2em'}}
                        >
                            {dataItem.databaseName}
                        </ListItemElement>
                        <ListItemElement
                            icon={dataItem.evoOrganisationId ? <CheckboxTickedFilled accent={ProjectAccents.CareCompliance}/> : <CheckboxEmptyLined accent={ProjectAccents.CareCompliance} />}
                            style={{marginLeft: "2em"}}
                        >
                            Evo Linked
                        </ListItemElement>
                    </>
                )}
                data={customerData}
            />
            {alert ? 
                <Alert
                    className={styles.CustomerListAlert}
                    closeTransition="fade"
                    type={alert.type}
                    showDuration={1200}
                    onClosed={() => setAlert(undefined)}
                >
                    {alert.message}
                </Alert> : undefined
            }
        </>
    }

    return (
        <>
            {content}
            {selectedCustomerForMenu ?
                <CustomerMenuModal
                    selectedCustomer={selectedCustomerForMenu}
                    handleClose={() => {
                        setSelectedCustomerForMenu(undefined)
                    }}
                    setAlert={setAlert}
                    reloadCustomerData={loadCustomerData}
                /> : undefined
            }
            {selectedCustomerForInfo ?
                <CustomerInfoModal
                    selectedCustomer={selectedCustomerForInfo}
                    handleClose={() => setSelectedCustomerForInfo(undefined)}
                /> : undefined
            }
        </>
    )

}

export default CustomerList;
